<script setup lang="ts">
import {
  nextTick,
  onActivated,
  onDeactivated,
  onMounted,
  onUnmounted,
  useTemplateRef,
  watch,
} from "vue";

const model = defineModel<string>({ required: true });

const props = defineProps<{ autofocus?: boolean }>();

defineExpose({ focus });

const textRef = useTemplateRef("text");

let widthObserver: ResizeObserver | null = null;
let lastObservedWidth = 0;

onMounted(start);
onActivated(start);

onUnmounted(stop);
onDeactivated(stop);

watch(model, adjustHeight);

function start() {
  adjustHeight();
  if (props.autofocus) {
    focus();
  }
  window.addEventListener("resize", adjustHeight);
  observeWidth();
}

function stop() {
  window.removeEventListener("resize", adjustHeight);
  widthObserver?.disconnect();
  widthObserver = null;
}

// scrollHeight depends on the current width, so re-run adjustHeight whenever
// the textarea's width changes (e.g., side panel finishes opening, parent
// resized, layout settles after mount). Listening only to window 'resize' is
// not enough.
function observeWidth() {
  if (!textRef.value || widthObserver) return;
  lastObservedWidth = textRef.value.offsetWidth;
  widthObserver = new ResizeObserver((entries) => {
    const width = entries[0]?.contentRect.width ?? 0;
    if (width !== lastObservedWidth) {
      lastObservedWidth = width;
      adjustHeight();
    }
  });
  widthObserver.observe(textRef.value);
}

function focus() {
  textRef.value?.focus();
}

function unfocus() {
  textRef.value?.blur();
}

function newline(event: KeyboardEvent) {
  if (!event.altKey && !event.shiftKey) {
    event.preventDefault();
    unfocus();
  }
}

function adjustHeight() {
  // Sets height to 0 and then computes it manually depending on remaining scroll height
  // and border widths. As a result textarea is adaptively resizing depending on it's content height.
  if (!textRef.value) return;

  // Adjust height of text area right after DOM is updated for correct calculation
  void nextTick(() => {
    if (textRef.value) {
      const computedStyle = window.getComputedStyle(textRef.value);
      const borderTopWidth = computedStyle.borderTopWidth;
      const borderBottomWidth = computedStyle.borderBottomWidth;

      textRef.value.style.height = "0";
      textRef.value.style.height = `calc(${borderTopWidth} + ${borderBottomWidth} + ${textRef.value.scrollHeight}px)`;
    }
  });
}
</script>

<!-- eslint-disable vuejs-accessibility/form-control-has-label-->
<template>
  <textarea
    v-bind="$attrs"
    ref="text"
    v-model="model"
    class="textarea"
    @click.stop
    @pointerdown.stop
    @keydown.esc.stop="unfocus"
    @keydown.enter="newline"
  />
</template>

<style scoped lang="scss">
@use "@/styles/font";
@use "@/styles/variables";
@use "@/styles/variables/colors";

.textarea {
  padding: 0.5rem;
  width: 100%;
  font-size: font.$size-normal;
  font-weight: font.$weight-bold;
  border-radius: variables.$objective-card-border-radius;
  border: 1px solid transparent;
  resize: none;
  overflow: hidden;
  margin: 0;
  overflow-wrap: anywhere;

  &:not([readonly]) {
    cursor: text;
  }

  &:focus {
    outline: none;
    border: 1px solid colors.$main;
  }

  &.error {
    border-color: colors.$destructive;
  }

  &::placeholder {
    color: colors.$text-placeholder;
    font-weight: font.$weight-medium;
  }
}
</style>
